import { Button } from '@affine/component';
import { Modal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useEffect, useMemo, useState } from 'react';
import { useWebUpdater } from './hooks/use-web-updater';
import { useLiveData, useService } from '@toeverything/infra';
import { I18nService } from '../modules/i18n';
import * as styles from './web-update-prompt.css';
import IconFont from '@ys/repo-ui/base/IconFont';

// 本地存储键名
const UPDATE_PROMPT_STORAGE_KEY = 'web-update-prompt';
const MAX_REMINDER_COUNT = 3;

interface UpdatePromptStorage {
  reminderCount: number;
  lastReminderDate: string;
  dismissedVersions: string[];
}

// 获取存储的提醒信息
const getStoredPromptInfo = (): UpdatePromptStorage => {
  try {
    const stored = localStorage.getItem(UPDATE_PROMPT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to parse stored prompt info:', error);
  }
  return {
    reminderCount: 0,
    lastReminderDate: '',
    dismissedVersions: [],
  };
};

// 保存提醒信息
const saveStoredPromptInfo = (info: UpdatePromptStorage) => {
  try {
    localStorage.setItem(UPDATE_PROMPT_STORAGE_KEY, JSON.stringify(info));
  } catch (error) {
    console.error('Failed to save prompt info:', error);
  }
};

// 检查是否应该显示提醒
const shouldShowReminder = (
  version: string,
  isForceUpdate: boolean
): boolean => {
  if (isForceUpdate) {
    return true; // 强制更新总是显示
  }

  const stored = getStoredPromptInfo();
  const today = new Date().toDateString();

  // 如果今天已经提醒过，不再提醒
  if (stored.lastReminderDate === today) {
    return false;
  }

  // 如果已经达到最大提醒次数，不再提醒
  if (stored.reminderCount >= MAX_REMINDER_COUNT) {
    return false;
  }

  // 如果这个版本已经被用户忽略过，不再提醒
  if (stored.dismissedVersions.includes(version)) {
    return false;
  }

  return true;
};

export const WebUpdatePrompt = () => {
  const { updateAvailable, updateInfo, handleRefresh, handleDismiss } =
    useWebUpdater();
  const t = useI18n();
  const i18n = useService(I18nService).i18n;
  const currentLanguage = useLiveData(i18n.currentLanguage$);
  const [showPrompt, setShowPrompt] = useState(false);
  const updateContent = useMemo(() => {
    const langId = currentLanguage?.key;
    if (langId === 'zh-Hans') {
      return updateInfo?.updateContent?.updateContentZH;
    } else {
      return updateInfo?.updateContent?.updateContentEN;
    }
  }, [currentLanguage, updateInfo?.updateContent]);

  useEffect(() => {
    if (updateAvailable && updateInfo?.latestVersion) {
      const isForceUpdate = updateInfo.isForceUpdate || false;
      const shouldShow = shouldShowReminder(
        updateInfo.latestVersion,
        isForceUpdate
      );
      setShowPrompt(shouldShow);
    }
  }, [updateAvailable, updateInfo]);

  const handleDismissClick = () => {
    if (updateInfo?.latestVersion) {
      const stored = getStoredPromptInfo();
      const today = new Date().toDateString();

      // 更新提醒信息
      const newInfo: UpdatePromptStorage = {
        reminderCount: stored.reminderCount + 1,
        lastReminderDate: today,
        dismissedVersions: [
          ...stored.dismissedVersions,
          updateInfo.latestVersion,
        ],
      };

      saveStoredPromptInfo(newInfo);
    }

    handleDismiss();
    setShowPrompt(false);
  };

  const handleRefreshClick = () => {
    if (updateInfo?.latestVersion) {
      const stored = getStoredPromptInfo();
      const today = new Date().toDateString();

      // 用户点击刷新，重置提醒计数
      const newInfo: UpdatePromptStorage = {
        reminderCount: 0,
        lastReminderDate: today,
        dismissedVersions: stored.dismissedVersions.filter(
          v => v !== updateInfo.latestVersion
        ),
      };

      saveStoredPromptInfo(newInfo);
    }

    handleRefresh();
  };

  if (!updateAvailable || !updateInfo?.latestVersion || !showPrompt) {
    return null;
  }

  const isForceUpdate = updateInfo.isForceUpdate || false;

  return (
    <Modal
      open={showPrompt}
      onOpenChange={isForceUpdate ? undefined : handleDismissClick}
      width={400}
      withoutCloseButton={true}
      contentOptions={{
        style: { padding: '24px' },
        className: styles.webUpdatePromptContent,
      }}
    >
      <div>
        <div className={styles.webUpdatePromptHeader}>
          <div className={styles.webUpdatePromptHeaderLeft}>
            {t['com.affine.webUpdate.title']?.() || '发现新版本'}
          </div>
          {!isForceUpdate && (
            <IconFont
              name="close-hoap7ooa"
              size={24}
              onClick={handleDismissClick}
              className={styles.webUpdatePromptHeaderRight}
            />
          )}
        </div>

        <p className={styles.webUpdatePromptdesc}>
          {t['com.affine.webUpdate.message']?.() ||
            '检测到新版本已发布，是否立即刷新页面？'}
        </p>
        {updateContent && (
          <div className={styles.webUpdatePromptdescContent}>
            <div className={styles.webUpdatePromptdescContentTitle}>
              {t['com.affine.webUpdate.updateContent']?.() || '更新内容：'}
            </div>
            <div className={styles.webUpdatePromptdescContentList}>
              {updateContent}
            </div>
          </div>
        )}
        <div
          className={styles.webUpdatePromptFooter}
          style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}
        >
          {!isForceUpdate && (
            <Button
              onClick={handleDismissClick}
              className={`${styles.webUpdatePromptFooterButton} ${styles.webUpdatePromptFooterButtonSecondary}`}
            >
              {t['com.affine.webUpdate.later']?.() || '稍后'}
            </Button>
          )}
          <Button
            onClick={handleRefreshClick}
            className={styles.webUpdatePromptFooterButton}
          >
            {t['com.affine.webUpdate.refresh']?.() || '立即刷新'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
